import React, { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import theme from "lib/material-ui/theme"
import Slide from "@mui/material/Slide"
import { styled } from "@mui/material/styles"
import Stack from "@mui/material/Stack"
import { QRCodeSVG } from "qrcode.react"
import { SHOW_SHARE_PROFILE_MODAL } from "constants/events"
import { generateQrCanvas } from "utils/qrCodeUtils"
import { useAppSelector } from "appRedux/hooks"
import { updateShowShareProfileModal } from "appRedux/slices/globalSlice"
import useEventListener from "hooks/useEventListener"
import useCopyToClipboard from "hooks/useCopyToClipboard"
import useBackListener from "hooks/useBackListener"
import CancelIcon from "components/atoms/_icons/CancelIcon"
import CopyIcon from "components/atoms/_icons/CopyIcon"
import DownloadIcon from "components/atoms/_icons/DownloadIcon"
import SendIcon from "components/atoms/_icons/SendIcon"
import TickIconWithBorder from "components/atoms/_icons/TickIconWithBorder"

const gradientStartColor = theme.palette.primary[5] as string
const gradientEndColor = theme.palette.primary[6] as string

export default function ShareProfileModal() {
  const [copied, setCopied] = useState<boolean>(false)

  const currentPageInfo = useAppSelector((state) => state.global.currentPageInfo)
  const showShareProfileModal = useAppSelector((state) => state.global.showShareProfileModal)

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const qrCodeRef = React.useRef<SVGSVGElement | null>(null)

  const username = useMemo(() => currentPageInfo?.dynamicPathParts?.handle, [currentPageInfo])

  const { copiedText, copyToClipboard } = useCopyToClipboard(showShareProfileModal)

  useEventListener(document, SHOW_SHARE_PROFILE_MODAL, handleShowShareProfileModal)

  useBackListener(showShareProfileModal, handleBackListenerCallback)

  useEffect(() => {
    setCopied(!!copiedText)
  }, [copiedText])

  function handleBackListenerCallback() {
    dispatch(updateShowShareProfileModal(false))
  }

  function handleShowShareProfileModal() {
    dispatch(updateShowShareProfileModal(true))
  }

  function handleCloseModal() {
    dispatch(updateShowShareProfileModal(false))
    navigate(-1)
  }

  async function handleCopy() {
    await copyToClipboard(getProfileLink())
  }

  async function handleDownload() {
    if (!qrCodeRef.current || !username) return
    const blob = await generateQrCanvas(qrCodeRef.current, username, { gradientStartColor, gradientEndColor })
    if (!blob) return

    const url = URL.createObjectURL(blob as Blob)
    const link = document.createElement("a")
    link.href = url
    link.download = getImageName()
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  async function handleShare() {
    if (!navigator.canShare) {
      console.error("Sharing is not supported on this device.")
      return
    }
    try {
      await navigator.share({ url: getProfileLink() })
    } catch (error) {
      console.error("Sharing failed", error)
    }
  }

  function getImageName() {
    return `wisham-${username}-qr-code.png`
  }

  function getProfileLink() {
    if (!username) return ""
    return window.location.origin + "/public/user/" + username
  }

  return (
    <Slide
      id="share-profile-modal"
      direction="left"
      in={showShareProfileModal}
      timeout={150}
      easing={{
        enter: "ease-in-out",
        exit: "linear"
      }}
      style={{
        width: "100%",
        maxWidth: "var(--max-container-width)",
        position: "fixed",
        top: 0,
        zIndex: 10000
      }}
    >
      <Container>
        <TopVector/>
        <CloseIconWrapper onClick={handleCloseModal}>
          <CancelIcon/>
        </CloseIconWrapper>
        <QRCodeContainer>
          <QRCodeSVG
            ref={qrCodeRef}
            value={getProfileLink()}
            bgColor="transparent"
            fgColor="#FFFFFF"
            size={(window.innerWidth > 440 ? 440 : window.innerWidth) - 104}
            marginSize={3}
            minVersion={10}
            style={{
              borderRadius: 4
            }}
          />
          <span>{username}@</span>
        </QRCodeContainer>
        <ButtonsWrapper>
          <Button onClick={handleCopy}>
            {copied ? <TickIconWithBorder/> : <CopyIcon/>}
            {copied ? t("general.copied") : t("general.copy")}
          </Button>
          <Button onClick={handleDownload}>
            <DownloadIcon/>
            {t("general.download")}
          </Button>
          <Button onClick={handleShare}>
            <SendIcon/>
            {t("general.share")}
          </Button>
        </ButtonsWrapper>
        <BottomVector/>
      </Container>
    </Slide>
  )
}

const Container = styled(Stack)(({ theme }) => ({
  width: "100vw",
  maxWidth: "var(--max-container-width)",
  height: "100vh",
  alignItems: "center",
  justifyContent: "center",
  gap: "12px",
  background: `linear-gradient(to bottom, ${gradientStartColor}, ${gradientEndColor})`,

  "@supports (height: 100dvh)": {
    height: "calc(100dvh)"
  }
}))

const CloseIconWrapper = styled(Stack)(({ theme }) => ({
  width: "40px",
  height: "40px",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "50%",
  overflow: "hidden",
  cursor: "pointer",
  position: "absolute",
  top: "12px",
  left: "16px",

  "& svg": {
    zIndex: 1,

    "& path": {
      fill: "#FFFFFF !important"
    }
  },

  "&::before": {
    content: "''",
    width: "100%",
    height: "100%",
    position: "absolute",
    top: 0,
    left: 0,
    backgroundColor: theme.palette.primary[6],
    opacity: "0.4"
  }
}))

const QRCodeContainer = styled(Stack)(({ theme }) => ({
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: "#FCF9FF1A",
  borderRadius: "4px",
  color: theme.palette.gray[1],
  fontSize: "1.7rem",
  fontWeight: 300,

  "& span": {
    marginBottom: "10px"
  }
}))

const TopVector = styled("div")(({ theme }) => ({
  width: "440px",
  height: "250px",
  position: "absolute",
  top: 0,
  right: 0,
  overflow: "hidden",

  "&::before": {
    content: "''",
    width: "100%",
    height: "100%",
    position: "absolute",
    top: 0,
    right: 0,
    backgroundImage: "url('/static/images/abstract-vectors/abstract-path-1.svg')",
    backgroundRepeat: "no-repeat",
    transform: "scale(-1.1, -1.1) translate(-46px, 50px)"
  },

  "&::after": {
    content: "''",
    width: "100%",
    height: "100%",
    position: "absolute",
    top: 0,
    right: 0,
    backgroundImage: "url('/static/images/abstract-vectors/abstract-path-2.svg')",
    backgroundRepeat: "no-repeat",
    transform: "scale(-1.1, -1.1) translate(-90px, 44px)",
    mixBlendMode: "color-burn"
  }
}))

const BottomVector = styled("div")(({ theme }) => ({
  width: "375px",
  height: "196px",
  position: "absolute",
  bottom: 0,
  left: 0,
  overflow: "hidden",

  "&::before": {
    content: "''",
    width: "100%",
    height: "100%",
    position: "absolute",
    bottom: 0,
    right: 0,
    backgroundImage: "url('/static/images/abstract-vectors/abstract-path-1.svg')",
    backgroundRepeat: "no-repeat",
    transform: "translate(0px, 6px)"
  },

  "&::after": {
    content: "''",
    width: "100%",
    height: "100%",
    position: "absolute",
    bottom: 0,
    right: 0,
    backgroundImage: "url('/static/images/abstract-vectors/abstract-path-2.svg')",
    backgroundRepeat: "no-repeat",
    transform: "translate(-44px, 4px)",
    mixBlendMode: "color-burn"
  }
}))

const ButtonsWrapper = styled(Stack)(() => ({
  width: "100%",
  maxWidth: "var(--max-container-width)",
  padding: "0 52px",
  flexDirection: "row-reverse",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "4px",
  zIndex: 1
}))

const Button = styled("button")(({ theme }) => ({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  gap: "8px",
  color: theme.palette.gray[1],
  border: "none",
  outline: "none",
  backgroundColor: "#FCF9FF1A",
  borderRadius: "8px",
  padding: "12px 0",
  cursor: "pointer",

  "& svg": {
    stroke: theme.palette.gray[1]
  }
}))
